import { Controller } from '@hotwired/stimulus'

/**
 * LinkCheckerController
 *
 * Checks if a sponsor's URL returns a 200 status code.
 * Uses a backend endpoint to avoid CORS issues with external URLs.
 *
 * Targets:
 * - button: The check button element
 * - status: The status badge display element
 *
 * Values:
 * - url: The sponsor's external URL to check
 * - checkUrl: The backend endpoint to verify the URL
 */
export default class extends Controller {
  static targets = ['button', 'status']
  static values = {
    url: String,
    checkUrl: String
  }

  /**
   * Check the URL status via backend proxy
   * @param {Event} event - Click event
   */
  async check(event) {
    event.preventDefault()

    this.setLoading(true)
    this.clearStatus()

    try {
      const response = await fetch(this.checkUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showStatus(data.status_code, data.success)
      } else {
        this.showError('Check failed')
      }
    } catch (error) {
      console.error('Error checking link:', error)
      this.showError('Network error')
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Display the status result with appropriate badge
   * @param {number} statusCode - HTTP status code returned
   * @param {boolean} success - Whether the URL returned 200
   */
  showStatus(statusCode, success) {
    const badgeClass = success ? 'bg-success' : 'bg-danger'
    const icon = success ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'

    this.statusTarget.innerHTML = `
      <span class="badge ${badgeClass}">
        <i class="${icon} me-1" aria-hidden="true"></i>${statusCode}
      </span>
    `
  }

  /**
   * Display an error message
   * @param {string} message - Error message to display
   */
  showError(message) {
    this.statusTarget.innerHTML = `
      <span class="badge bg-warning text-dark">
        <i class="fa-solid fa-exclamation-triangle me-1" aria-hidden="true"></i>${message}
      </span>
    `
  }

  /**
   * Clear the status display
   */
  clearStatus() {
    this.statusTarget.innerHTML = ''
  }

  /**
   * Toggle loading state on button
   * @param {boolean} loading - Whether loading is in progress
   */
  setLoading(loading) {
    const buttonText = this.buttonTarget.querySelector('span')?.textContent || ''
    if (loading) {
      this.buttonTarget.disabled = true
      this.buttonTarget.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span><span class="ms-1">${buttonText}</span>`
    } else {
      this.buttonTarget.disabled = false
      this.buttonTarget.innerHTML = `<i class="fa-solid fa-sync-alt" aria-hidden="true"></i><span class="ms-1">${buttonText}</span>`
    }
  }

  /**
   * Get the CSRF token from the meta tag
   * @returns {string} CSRF token
   */
  getCSRFToken() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')
    return csrfToken ? csrfToken.content : ''
  }
}
